import { SandpitSite } from "@anu-its/cat-grpc-pb/js/anu/cat_app/sandpit_site_pb";
import { Badge, Button, Row, Table } from "@anu-its/ts-react-components";
import React, { useCallback, useEffect, useState } from "react";
import {
  dashboardSiteStatuses,
  siteStatusLabel,
  statusBadgeVariant,
} from "../constants/site-status";
import { SiteStatus } from "@anu-its/cat-grpc-pb/js/anu/cat/enums_pb";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { GetSandpitSites } from "../api/Sites";
import { LoadingOverlay } from "../components/Loading";
import { GearSharp } from "@anu-its/ts-react-components/icons/simple";
import { TableFilters } from "./TableFilters";
import { CanvasLink } from "@/app/sites/[site_id]/helpers/CanvasLink";
import { useRouter } from "next/navigation";
import CreateSandpitButton from "@/app/dashboard/actions/CreateSandpitButton";
import { useUserStore } from "@/app/providers/UserStore";
import { NewFilter } from "@/app/filters/interface";

export const SandpitSiteTable = ({
  page,
  pageSize,
  resetPage,
  handleTotalResults,
}: {
  page: number;
  pageSize: number;
  resetPage: () => void;
  handleTotalResults: (totalResults: number) => void;
}) => {
  const { userIsSuperAdmin } = useUserStore();
  const router = useRouter();

  const [nextCursor, setNextCursor] = useState("");
  const [siteData, setSiteData] = useState([] as Row[]);
  const [statusFilterList, setStatusFilterList] = useState<SiteStatus[]>(
    dashboardSiteStatuses,
  );
  const [sandpitOwner, setSandpitOwner] = useState<string>("");

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["GetSandpitSites", page, statusFilterList, sandpitOwner],
    queryFn: () => {
      return sandpitOwner
        ? GetSandpitSites(pageSize, nextCursor, statusFilterList, sandpitOwner)
        : GetSandpitSites(pageSize, nextCursor, statusFilterList);
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (data && !(data instanceof Error)) {
      setNextCursor(data.cursor);
      const mappedData = siteDataMapper(data.sites);
      setSiteData([...mappedData]);
      handleTotalResults(Number(data.totalResults));
    }
  }, [data, handleTotalResults]);

  const siteDataMapper = (sites: SandpitSite[]) => {
    return sites.map((site) => ({
      name: site.displayName,
      owner: site.owner?.name?.display,
      id: site.siteId,
      status: site.status,
    }));
  };

  const handleFiltersChange = useCallback(
    (newFilters: NewFilter) => {
      resetPage();
      setNextCursor("");
      setStatusFilterList(newFilters.statusFilterList as SiteStatus[]);
      setSandpitOwner(newFilters.sandpitOwner as string);
    },
    [resetPage],
  );

  const columns = [
    { field: "name", headerName: "Name" },
    { field: "owner", headerName: "Owner" },
    { field: "id", headerName: "ID" },
    {
      field: "status",
      headerName: "Status",
      render: (value: SiteStatus) => {
        return (
          <Badge size="md" variant={statusBadgeVariant[value]}>
            {siteStatusLabel(value)}
          </Badge>
        );
      },
    },
    {
      field: "manage",
      headerName: "Manage",
      render: (
        _: string,
        row: { name: string; owner: string; id: string; status: SiteStatus },
      ) => {
        return (
          <>
            <Button
              variant="tertiary"
              classNames="!border-none"
              href={`/sites/${row.id}`}
              size="small"
              aria-label={`Navigate to ${row.name || row.id} details page`}
            >
              <GearSharp size="lg" />
            </Button>
            <CanvasLink
              siteId={row.id}
              status={row.status}
              isIconOnly={true}
              aria-label={`View ${row.name || row.id} in Canvas`}
            />
          </>
        );
      },
    },
  ];

  if (isLoading) return <LoadingOverlay />;

  if (error || data instanceof Error) {
    router.push("/error?errorType=data");
  }

  return (
    <>
      {isFetching && <LoadingOverlay />}

      <div className="pb-2">
        <CreateSandpitButton />
      </div>

      {/* Table filters */}
      {userIsSuperAdmin && (
        <TableFilters
          onFiltersChange={handleFiltersChange}
          showStatus={true}
          showSandpitSitesForOwnerSearch={true}
        />
      )}

      {/* Render table or no results */}
      {siteData.length > 0 ? (
        <Table columns={columns} rows={siteData} />
      ) : (
        // Handle "no results" state here
        <>
          <div>No results found.</div>
          <a
            target="_blank"
            href="https://servicedesk.anu.edu.au/sp?id=sc_cat_item&sys_id=b041e3bfdb7f1054974096888a96194f&sysparm_category=6fa33c911bd00510e8bda8aa274bcb2d"
          >
            Contact ITS to request a sandpit site
          </a>
        </>
      )}
    </>
  );
};
